/**
 * SignUp (Authentication) — sign-up page rebuilt on composition primitives.
 *
 * Layout tree:
 *   AuthShell
 *     CenteredCard (maxWidthPx=420, radius=xl, paddingX=m, paddingY=xl,
 *                   gap=xl, cardAs=form, decoration=Glow when ready)
 *       LogoAssemble
 *       Subtitle (raw heading-level paragraph via text token)
 *       Stack gap=m (FieldBlock)
 *         Stack gap=s  (phone + password — primary credentials)
 *         SecondaryFieldGroup  (email + promocode)
 *         ServerError (general, near button)
 *         Submit Button
 *       ConsentParagraph (raw body.s with inline Links)
 *     FooterRow (under-card 420 row with cross-page links)
 *
 * Form state + validation are owned by react-hook-form (yup resolver).
 * Field errors are client-side (email format); server errors are opaque
 * backend messages surfaced in a single `serverError` slot near the submit
 * button — never attributed to a specific field.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {Trans, useTranslation} from 'react-i18next';
import {useForm, Controller} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import * as yup from 'yup';

import {LogoAssemble} from 'ui/components/molecules/LogoAssemble';
import {Input} from 'ui/components/atoms/Input';
import {Button} from 'ui/components/atoms/Button';
import {Link} from 'ui/components/atoms/Link';
import {Glow} from 'ui/components/atoms/Glow';
import {Stack} from 'ui/components/layout/Stack';
import {Row} from 'ui/components/layout/Row';
import {CenteredCard} from 'ui/components/composition/CenteredCard';
import {text} from 'ui/theme/typography';

import {AuthShell} from '../_shell';

export interface SignUpValues {
  phone?: string;
  password?: string;
  email?: string;
  promocode?: string;
}

export interface SignUpProps {
  /** Pre-seed field values; unspecified keys start empty. */
  initialValues?: SignUpValues;
  /**
   * Opaque server error (busy phone, busy email, invalid promo code, …)
   * shown near the submit button. Not tied to a field — the backend returns
   * machine codes we can't reliably attribute to phone/email/password.
   */
  serverError?: string;
  /** Mid-request override; submitting disables submit and rotates the label. */
  submitState?: 'idle' | 'submitting';
  onSubmit?: (data: {phone: string; password: string; email: string; promocode: string}) => void;
  onSignInClick?: () => void;
  onLogoClick?: () => void;
  /**
   * Second-level domain for the footer link + label (e.g. 'adsensor.ru' /
   * 'adsensor.io'). The container passes the build's domain so both the URL
   * and the visible text stay correct across .ru/.io.
   */
  adsensorDomain?: string;
}

// ── Inline typographic wrappers ────────────────────────────────────────
// Two raw text blocks live in the form body — the subtitle and the consent
// paragraph. Each one is a single styled element binding a typography
// token + a surface text color.

const Subtitle = styled('p')(({theme}) => ({
  ...text('body.l'),
  color: theme.palette.surface.onSurface,
  textAlign: 'center',
  margin: 0,
  width: '100%',
}));

const ConsentParagraph = styled('p')(({theme}) => ({
  ...text('body.s'),
  color: theme.palette.surface.onSurfaceLight,
  textAlign: 'left',
  margin: 0,
  width: '100%',
}));

// General server-error line, centered above the submit button.
const ServerError = styled(Box)(({theme}) => ({
  ...text('body.s'),
  color: theme.palette.bad.main,
  textAlign: 'center',
  width: '100%',
}));

// FooterRow: same shape as in SignIn. Extends Row to apply the 420 cap
// + paddingTop/X used to align the under-card row with the card itself.
const FooterRow = styled(Row)(({theme}) => ({
  width: '100%',
  maxWidth: 420,
  paddingTop: theme.spacing(4),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  boxSizing: 'border-box',
}));

// SecondaryFieldGroup — the email + promocode pair, separated from the
// primary phone/password pair by a one-off paddingTop.
const SecondaryFieldGroup = styled(Box)(({theme}) => ({
  paddingTop: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
  boxSizing: 'border-box',
}));

// ── Component ──────────────────────────────────────────────────────────

export function SignUp({
  initialValues,
  serverError,
  submitState = 'idle',
  onSubmit,
  onSignInClick,
  onLogoClick,
  adsensorDomain = 'adsensor.ru',
}: SignUpProps) {
  const {t} = useTranslation();

  // Email is required and format-checked client-side before we hit the
  // server. phone/password emptiness is handled by the button gate.
  const schema = yup.object({
    phone: yup.string(),
    password: yup.string(),
    email: yup
      .string()
      .required(t('Укажите емейл', 'Укажите емейл'))
      .email(t('Введен некорректный email-адрес', 'Введен некорректный email-адрес')),
    promocode: yup.string(),
  });

  const {control, handleSubmit, watch, formState} = useForm({
    defaultValues: {
      phone: initialValues?.phone ?? '',
      password: initialValues?.password ?? '',
      email: initialValues?.email ?? '',
      promocode: initialValues?.promocode ?? '',
    },
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const {phone, password, email} = watch();
  const fieldsFilled = Boolean(phone) && Boolean(password) && Boolean(email);
  const isSubmitting = submitState === 'submitting';
  const hasFieldError = Object.keys(formState.errors).length > 0;
  const isValid = fieldsFilled && !isSubmitting;
  const showActionGlow = fieldsFilled && !hasFieldError && !serverError;

  const onValid = (values: {
    phone?: string;
    password?: string;
    email?: string;
    promocode?: string;
  }) => {
    onSubmit?.({
      phone: values.phone ?? '',
      password: values.password ?? '',
      email: values.email ?? '',
      promocode: values.promocode ?? '',
    });
  };

  return (
    <AuthShell onLogoClick={onLogoClick}>
      <CenteredCard
        maxWidthPx={420}
        radius='xl'
        paddingX='m'
        paddingY='xl'
        gap='xl'
        contentAlignX='center'
        cardAs='form'
        cardProps={{onSubmit: handleSubmit(onValid), noValidate: true}}
        decoration={showActionGlow ? <Glow color='good' /> : null}
      >
        <LogoAssemble prefix='signUp' />

        <Subtitle>
          {t(
            'Создайте аккаунт и проверьте свою рекламу на бесплатном триале',
            'Создайте аккаунт и проверьте свою рекламу на бесплатном триале',
          )}
        </Subtitle>

        <Stack gap='m'>
          <Stack gap='s'>
            <Controller
              name='phone'
              control={control}
              render={({field, fieldState}) => (
                <Input
                  {...field}
                  size='m'
                  type='tel'
                  fullWidth
                  leftIcon='call'
                  placeholder={t('Телефон', 'Телефон')}
                  autoComplete='tel'
                  errorMessage={fieldState.error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name='password'
              control={control}
              render={({field, fieldState}) => (
                <Input
                  {...field}
                  size='m'
                  type='password'
                  fullWidth
                  leftIcon='lock'
                  placeholder={t('Придумайте пароль', 'Придумайте пароль')}
                  autoComplete='new-password'
                  errorMessage={fieldState.error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
          </Stack>

          <SecondaryFieldGroup>
            <Controller
              name='email'
              control={control}
              render={({field, fieldState}) => (
                <Input
                  {...field}
                  size='m'
                  type='email'
                  fullWidth
                  leftIcon='mail'
                  placeholder={t('Емейл', 'Емейл')}
                  autoComplete='email'
                  errorMessage={fieldState.error?.message}
                  disabled={isSubmitting}
                />
              )}
            />
            <Controller
              name='promocode'
              control={control}
              render={({field}) => (
                <Input
                  {...field}
                  size='m'
                  fullWidth
                  leftIcon='redeem'
                  placeholder={t('Промокод (необязательно)', 'Промокод (необязательно)')}
                  disabled={isSubmitting}
                />
              )}
            />
          </SecondaryFieldGroup>

          {serverError && <ServerError role='alert'>{serverError}</ServerError>}

          <Button
            type='submit'
            variant='solid'
            color='good'
            size='m'
            fullWidth
            disabled={!isValid}
            loading={isSubmitting ? 'left' : false}
          >
            {isSubmitting
              ? t('Создаём аккаунт', 'Создаём аккаунт')
              : !fieldsFilled
                ? t('Заполните поля', 'Заполните поля')
                : t('Создать аккаунт', 'Создать аккаунт')}
          </Button>
        </Stack>

        <ConsentParagraph>
          {/*
           * Real document URLs recovered from the retired
           * `pages/Auth/EmailSignUp/Footer.tsx` (git show 25155e74^). There
           * was never a separate "условия использования" (terms of use)
           * document — that link had no source and pointed at a dead `#`,
           * so it's dropped entirely rather than left unfunctional.
           */}
          <Trans>
            Нажимая «Создать аккаунт», вы соглашаетесь с{' '}
            <Link href='/personal_data_processing.pdf' external>
              политикой обработки персональных данных
            </Link>
            ,{' '}
            <Link href='/files_cookies_uses_policy.pdf' external>
              правилами cookies
            </Link>{' '}
            и{' '}
            <Link href='/offer.pdf' external>
              офертой
            </Link>
            .
          </Trans>
        </ConsentParagraph>
      </CenteredCard>

      <FooterRow justify='between'>
        <Button variant='ghost' color='neutral' onClick={onSignInClick} disabled={isSubmitting}>
          {t('Уже есть аккаунт', 'Уже есть аккаунт')}
        </Button>
        <Button
          variant='ghost'
          color='neutral'
          rightIcon='arrow_outward'
          onClick={() => window.open(`https://${adsensorDomain}`, '_blank', 'noopener')}
        >
          {adsensorDomain}
        </Button>
      </FooterRow>
    </AuthShell>
  );
}
